import { useState } from 'react';

import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconContainerVariant } from '@components/icon-fancy-container/icon-fancy-container';
import { IconAI } from '@components/icons/ai';
import { IconBoolean } from '@components/icons/boolean';
import { IconBulk } from '@components/icons/bulk';
import { IconCompany } from '@components/icons/company';
import { IconContract } from '@components/icons/contract';
import { IconDate } from '@components/icons/date';
import { IconDocument } from '@components/icons/document';
import { IconEducation } from '@components/icons/education';
import { IconElement } from '@components/icons/element';
import { IconEmail } from '@components/icons/email';
import { IconEndpoint } from '@components/icons/endpoint';
import { IconEqualsNot } from '@components/icons/equals-not';
import { IconFile } from '@components/icons/file';
import { IconFiles } from '@components/icons/files';
import { IconFilter } from '@components/icons/filter';
import { IconFixPosOn } from '@components/icons/fix-pos-on';
import { IconFolder } from '@components/icons/folder';
import { IconForm } from '@components/icons/form';
import { IconGroup } from '@components/icons/group';
import { IconHelp } from '@components/icons/help';
import { IconHourglass } from '@components/icons/hourglass';
import { IconModeDark } from '@components/icons/icon-mode-dark';
import { IconModeLight } from '@components/icons/icon-mode-light';
import { IconPersonDetails } from '@components/icons/icon-person-details';
import { IconImage } from '@components/icons/image';
import { IconInbox } from '@components/icons/inbox';
import { IconInput } from '@components/icons/input';
import { IconIntegration } from '@components/icons/integration';
import { IconInternalTasklist } from '@components/icons/internal-tasklist';
import { IconIssues } from '@components/icons/issues';
import { IconJob } from '@components/icons/job';
import { IconJobboard } from '@components/icons/jobboard';
import { IconLabel } from '@components/icons/label';
import { IconLanguage } from '@components/icons/language';
import { IconLibrary } from '@components/icons/library';
import { IconList } from '@components/icons/list';
import { IconListDynamic } from '@components/icons/list-dynamic';
import { IconLocation } from '@components/icons/location';
import { IconLoop } from '@components/icons/loop';
import { IconMarketplace } from '@components/icons/marketplace';
import { IconMove } from '@components/icons/move';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconPerson } from '@components/icons/person';
import { IconPersonAdd } from '@components/icons/person-add';
import { IconPersonRemove } from '@components/icons/person-remove';
import { IconSms } from '@components/icons/phone-sms';
import { IconPin } from '@components/icons/pin';
import { IconPinned } from '@components/icons/pinned';
import { IconReject } from '@components/icons/reject';
import { IconReport } from '@components/icons/report';
import { IconScorecard } from '@components/icons/scorecard';
import { IconSection } from '@components/icons/section';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSend } from '@components/icons/send';
import { IconSSO } from '@components/icons/sso';
import { IconStages } from '@components/icons/stages';
import { IconStar } from '@components/icons/star';
import { IconStatistics } from '@components/icons/statistics';
import { IconStatusIndicatorLarge } from '@components/icons/status-indicator-large';
import { IconTeam } from '@components/icons/team';
import { IconTemplate } from '@components/icons/template';
import { IconTime } from '@components/icons/time';
import { IconWorkspace } from '@components/icons/workspace';
import { IconWorkspaceAlt } from '@components/icons/workspace-alt';
import { IconZoomIn } from '@components/icons/zoom-in';
import { PopoverButton } from '@components/popover-button';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Color } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export const ICON_CHOOSER_ICONS = {
  IconWorkspace,
  IconAI,
  IconBulk,
  IconBoolean,
  IconContract,
  IconCompany,
  IconDate,
  IconDocument,
  IconPerson,
  IconTeam,
  IconPersonAdd,
  IconPersonRemove,
  IconFile,
  IconReport,
  IconReject,
  IconStar,
  IconPinned,
  IconPin,
  IconEmail,
  IconStages,
  IconTemplate,
  IconInternalTasklist,
  IconSms,
  IconSSO,
  IconScorecard,
  IconStatistics,
  IconSection,
  IconWorkspaceAlt,
  IconTime,
  IconOnboarding,
  IconMove,
  IconPersonDetails,
  IconMarketplace,
  IconLocation,
  IconLanguage,
  IconListDynamic,
  IconList,
  IconGroup,
  IconLibrary,
  IconLabel,
  IconJob,
  IconJobboard,
  IconIssues,
  IconImage,
  IconIntegration,
  IconInbox,
  IconHourglass,
  IconHelp,
  IconFilter,
  IconForm,
  IconFolder,
  IconFiles,
  IconElement,
  IconEndpoint,
  IconEducation,
  IconEqualsNot,
  IconFixPosOn,
  IconInput,
  IconLoop,
  IconSend,
  IconModeLight,
  IconModeDark,
  IconSelectMultiple,
  IconZoomIn,
};

export const ITEM_CHOOSER_COLORS: Color[] = [
  'purple50',
  'purple100',
  'indigo100',
  'blue100',
  'teal100',
  'green100',
  'orange100',
  'red100',
];

export const COLOR_TO_VARIANT: Partial<Record<Color, IconContainerVariant>> = {
  blue100: 'blue',
  green100: 'green',
  orange100: 'orange',
  purple100: 'purple',
  red100: 'red',
  teal100: 'teal',
  indigo100: 'indigo',
  purple50: 'gray',
};

export type IconName = keyof typeof ICON_CHOOSER_ICONS;

export type IconChooserValue = {
  color: Color;
  icon: IconName;
};

export type IconChooserProps = {
  defaultValue?: IconChooserValue;
  onChange: (value: IconChooserValue) => void;
};

export const IconChooser = ({
  defaultValue = { color: 'blue100', icon: 'IconWorkspace' },
  onChange,
}: IconChooserProps) => {
  const [value, setValue] = useState<IconChooserValue>(defaultValue);
  const IconComponent = ICON_CHOOSER_ICONS[value.icon];

  return (
    <PopoverButton
      placement="right-end"
      trigger={IconFancyContainer}
      triggerProps={{
        icon: <IconComponent color={value.color} />,
        variant: COLOR_TO_VARIANT[value.color],
        isClickable: true,
      }}
      contentStyle={{ maxWidth: pxToRem(288) }}
    >
      {({ close }) => (
        <IconChooserPopover
          color={value.color}
          icon={value.icon}
          onSelect={(color, icon, shouldClose) => {
            const newValue = { color, icon };
            onChange(newValue);
            setValue(newValue);
            shouldClose && close();
          }}
        />
      )}
    </PopoverButton>
  );
};

const IconChooserPopover = ({
  color: selectedColor,
  icon,
  onSelect,
}: {
  color: Color;
  icon: IconName;
  onSelect: (color: Color, icon: IconName, shouldClose: boolean) => void;
}) => {
  return (
    <Stack space={2}>
      <RowContainer>
        {ITEM_CHOOSER_COLORS.map((color, index) => (
          <IconButton
            size="md"
            icon={<IconStatusIndicatorLarge color={color} />}
            key={index}
            variant={color === selectedColor ? 'secondary-purple' : 'ghost-purple'}
            onClick={() => onSelect(color, icon, false)}
          />
        ))}
      </RowContainer>
      <Separator />
      <RowContainer>
        {Object.entries(ICON_CHOOSER_ICONS).map(([iconName, IconComponent]) => (
          <IconButton
            size="md"
            icon={
              <IconComponent color={iconName === icon ? selectedColor : 'buttonIconPositive'} />
            }
            key={iconName}
            variant={iconName === icon ? 'secondary-purple' : 'ghost-purple'}
            onClick={() => onSelect(selectedColor, iconName as IconName, true)}
          />
        ))}
      </RowContainer>
    </Stack>
  );
};

const RowContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 32px)',
  '> *': {
    justifySelf: 'center',
  },
});
